import { Link } from "react-router-dom";
import { ArrowRight, CalendarPlus } from "lucide-react";
import { useGet } from "@/lib/api";
import { PLACE_CAT, STATIC_IMG } from "@/lib/meta";
import { PhotoCredit } from "@/components/PhotoCredit";

import { PlaceCard } from "@/components/PlaceCard";
import { EventRow } from "@/components/EventCard";
import { WeatherWidget } from "@/components/WeatherWidget";
import { TideWidget } from "@/components/TideWidget";
import { HomeCompass } from "@/components/HomeCompass";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Faq, IslandStrip, Stats, TownTiles, Welcome } from "@/components/HomeSections";
import { WeatherAlerts } from "@/components/WeatherAlerts";
import { Skeleton } from "@/components/ui/skeleton";

const Hero = () => (
  <>
  <section className="relative h-[78svh] min-h-[520px] overflow-hidden md:h-[86vh] md:min-h-[560px]" data-testid="home-hero">
    <img src={STATIC_IMG.hero.src} alt="Nordseestrand am Oststrand von Norderney" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-tr from-black/65 via-black/25 to-transparent" />
    <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
      <p className="eyebrow rise w-fit bg-black/35 px-2.5 py-1 text-sand backdrop-blur-sm">Nordseeküste · Inseln · Fehnland</p>
      <h1 className="rise rise-1 mt-4 max-w-4xl text-5xl leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
        Moin. <span className="italic">Hier ist</span> Ostfriesland.
      </h1>
      <p className="rise rise-2 mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
        Veranstaltungen, Inseln, Radtouren und Wattwandern, dazu Wetter, Gezeiten und Wochenend-Tipps täglich aktualisiert. Dein Begleiter für Ostfriesland, von Norderney bis Leer.
      </p>
      <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
        <Link to="/entdecken" data-testid="hero-discover-btn" className="lift inline-flex items-center gap-2 bg-terracotta px-6 py-3.5 font-sub text-white hover:bg-terracotta/90">
          Jetzt entdecken <ArrowRight size={18} strokeWidth={1.5} />
        </Link>
        <Link to="/veranstaltungen" data-testid="hero-events-btn" className="lift inline-flex items-center gap-2 border border-white/40 bg-white/10 px-6 py-3.5 font-sub text-white backdrop-blur-md hover:bg-white/20">
          Veranstaltungen
        </Link>
      </div>
    </div>
  </section>
  <PhotoCredit credit={STATIC_IMG.hero.credit} className="mx-auto max-w-[1400px] px-5 pt-2 md:px-10" testid="home-hero-credit" />
  </>
);

const Section = ({ eyebrow, title, link, linkLabel, children, testid }) => (
  <section className="mx-auto max-w-[1400px] px-5 pt-12 md:px-10 md:pt-16" data-testid={testid}>
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 md:mb-8">
      <div>
        <p className="eyebrow text-terracotta">{eyebrow}</p>
        <h2 className="mt-2 text-3xl tracking-tight md:text-4xl">{title}</h2>
      </div>
      {link && <Link to={link} className="group flex items-center gap-2 font-sub text-sm hover:text-terracotta">{linkLabel}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></Link>}
    </div>
    {children}
  </section>
);

const Categories = () => (
  <div className="flex flex-wrap gap-3" data-testid="home-categories">
    {Object.entries(PLACE_CAT).map(([k, c]) => (
      <Link key={k} to={`/entdecken?kategorie=${k}`} data-testid={`home-cat-${k}`}
        className="lift flex items-center gap-2.5 border border-foreground/15 bg-white/50 px-5 py-3 font-sub text-sm hover:bg-white">
        <span className="h-2.5 w-2.5" style={{ background: c.color }} />{c.label}
      </Link>
    ))}
  </div>
);

const Featured = ({ places }) => {
  if (!places) return <Skeleton className="h-[600px]" />;
  const pick = [places.find((p) => p.category === "wattwandern"), ...places.filter((p) => p.category !== "wattwandern")].filter(Boolean);
  const [a, b, c, d, e] = [pick[0], pick.find((p) => p.category === "sehenswuerdigkeiten"), pick.find((p) => p.category === "gastronomie"), pick.find((p) => p.category === "natur"), pick.find((p) => p.category === "radtouren")];
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      {a && <PlaceCard place={a} tall className="md:col-span-7 md:row-span-2" />}
      {b && <PlaceCard place={b} className="md:col-span-5" />}
      {c && <PlaceCard place={c} className="md:col-span-5" />}
      {d && <PlaceCard place={d} className="md:col-span-4" />}
      {e && <PlaceCard place={e} className="md:col-span-8" />}
    </div>
  );
};

export default function Home() {
  const { data: places } = useGet("home-featured", "/home/featured", {});
  const { data: events, isLoading } = useGet("events", "/events", { limit: 6 });
  return (
    <div data-testid="home-page">
      <Hero />
      <div className="mx-auto max-w-[1400px] px-5 pt-8 empty:hidden md:px-10" data-testid="home-alerts"><WeatherAlerts compact /></div>
      <HomeCompass />
      <div className="mx-auto max-w-[1400px] px-5 pt-10 md:px-10"><NewsletterSignup testid="home-newsletter" /></div>
      <Welcome />
      <Section eyebrow="Heute an der Küste" title="Wetter, Wind & Wasser" link="/wetter" linkLabel="Alle Orte & Vorhersage" testid="home-weather">
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8"><WeatherWidget compact /></div>
          <div className="lg:col-span-4"><TideWidget /></div>
        </div>
      </Section>
      <Section eyebrow="Sieben Inseln, ein Wattenmeer" title="Die Ostfriesischen Inseln" link="/inseln" linkLabel="Alle Inseln & Fähren" testid="home-islands-section"><IslandStrip /></Section>
      <Section eyebrow="Vom Fischerdorf bis zur Hafenstadt" title="Beliebte Orte in Ostfriesland" link="/orte" linkLabel="Alle 20 Orte" testid="home-towns-section"><TownTiles /></Section>
      <Stats />
      <Section eyebrow="Was möchtest du erleben?" title="Nach Kategorie stöbern" testid="home-categories-section"><Categories /></Section>
      <Section eyebrow="Ausgewählt" title="Muss man gesehen haben" link="/entdecken" linkLabel="Alle Orte" testid="home-featured">
        <Featured places={places} />
      </Section>
      <Section eyebrow="Demnächst" title="Veranstaltungen in Ostfriesland" link="/veranstaltungen" linkLabel="Kalender öffnen" testid="home-events">
        {isLoading ? <Skeleton className="h-64" /> : <div className="border-t border-foreground/10">{events?.map((ev) => <EventRow key={ev.id} ev={ev} />)}</div>}
      </Section>
      <Section eyebrow="Gut zu wissen" title="Häufige Fragen zu Ostfriesland" testid="home-faq-section"><Faq /></Section>
      <section className="mx-auto max-w-[1400px] px-5 pt-12 md:px-10 md:pt-16">
        <div className="grid items-center gap-8 border border-foreground/15 bg-sand/40 p-10 md:grid-cols-[1fr_auto] md:p-14">
          <div>
            <p className="eyebrow text-terracotta">Für Vereine, Gastgeber & Veranstalter</p>
            <h2 className="mt-2 text-3xl tracking-tight md:text-4xl">Du organisierst etwas? Erzähl es allen.</h2>
            <p className="mt-3 max-w-xl text-muted-foreground">Registriere dich kostenlos und reiche deine Veranstaltung ein. Nach kurzer Prüfung erscheint sie im Kalender.</p>
          </div>
          <Link to="/einreichen" data-testid="home-submit-cta" className="lift inline-flex items-center gap-2 bg-ocean px-6 py-4 font-sub text-shell hover:bg-ocean/90">
            <CalendarPlus size={18} strokeWidth={1.5} /> Veranstaltung einreichen
          </Link>
        </div>
      </section>
    </div>
  );
}
